import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { AlertTriangle, Check, X, Clock, MessageSquare } from 'lucide-react';

import { API as API_URL, BACKEND_URL } from '../../utils/apiConfig';

export const AdminDisputesTab = () => {
  const { t } = useTranslation();
  const [disputes, setDisputes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [resolving, setResolving] = useState(null);
  const [adminNotes, setAdminNotes] = useState('');

  useEffect(() => { loadDisputes(); }, []);

  const loadDisputes = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API_URL}/disputes/admin/all`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setDisputes(res.data.disputes || []);
    } catch (err) {
      console.error('Failed to load disputes:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleResolve = async (disputeId, status) => {
    try {
      setResolving(disputeId);
      const token = localStorage.getItem('token');
      await axios.put(`${API_URL}/disputes/admin/${disputeId}/resolve`, {
        status,
        admin_notes: adminNotes || null,
      }, { headers: { Authorization: `Bearer ${token}` } });
      setAdminNotes('');
      setResolving(null);
      loadDisputes();
    } catch (err) {
      alert(err.response?.data?.detail || 'Error');
      setResolving(null);
    }
  };

  const filtered = filter === 'all' ? disputes : disputes.filter(d => d.status === filter);
  const openCount = disputes.filter(d => d.status === 'open').length;

  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-600" />
      </div>
    );
  }

  return (
    <div className="p-6" data-testid="admin-disputes-tab">
      {/* Summary cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <div className="bg-orange-50 border border-orange-200 rounded-xl p-4">
          <div className="text-sm text-orange-600 font-semibold">{t('disputes.status.open')}</div>
          <div className="text-2xl font-bold text-orange-700">{openCount}</div>
        </div>
        <div className="bg-green-50 border border-green-200 rounded-xl p-4">
          <div className="text-sm text-green-600 font-semibold">{t('disputes.status.resolved')}</div>
          <div className="text-2xl font-bold text-green-700">{disputes.filter(d => d.status === 'resolved').length}</div>
        </div>
        <div className="bg-red-50 border border-red-200 rounded-xl p-4">
          <div className="text-sm text-red-600 font-semibold">{t('disputes.status.rejected')}</div>
          <div className="text-2xl font-bold text-red-700">{disputes.filter(d => d.status === 'rejected').length}</div>
        </div>
      </div>

      {/* Filter */}
      <div className="flex gap-2 mb-6 flex-wrap">
        {['all', 'open', 'resolved', 'rejected'].map(f => (
          <button
            key={f}
            onClick={() => setFilter(f)}
            data-testid={`disputes-filter-${f}`}
            className={`px-3 py-2 rounded-lg text-sm font-semibold transition whitespace-nowrap ${
              filter === f ? 'bg-orange-600 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
          >
            {f === 'all' ? 'All' : t(`disputes.status.${f}`)} ({f === 'all' ? disputes.length : disputes.filter(d => d.status === f).length})
          </button>
        ))}
      </div>

      {/* Disputes list */}
      {filtered.length === 0 ? (
        <div className="text-center py-12 text-gray-400">
          <AlertTriangle size={48} className="mx-auto mb-3 opacity-30" />
          <p>{t('disputes.noDisputes')}</p>
        </div>
      ) : (
        <div className="space-y-4">
          {filtered.map((dispute) => (
            <div key={dispute.id} className={`bg-white rounded-xl border-2 p-5 ${
              dispute.status === 'open' ? 'border-orange-300' : 'border-gray-200'
            }`}>
              <div className="flex justify-between items-start mb-3">
                <div className="min-w-0 flex-1 mr-2">
                  <div className="flex items-center gap-2 mb-1 flex-wrap">
                    <span className={`px-2 py-0.5 rounded-full text-xs font-bold whitespace-nowrap ${
                      dispute.status === 'open' ? 'bg-orange-100 text-orange-700' :
                      dispute.status === 'resolved' ? 'bg-green-100 text-green-700' :
                      'bg-red-100 text-red-700'
                    }`}>
                      {t(`disputes.status.${dispute.status}`)}
                    </span>
                    <span className="text-sm text-gray-500">
                      {dispute.start_city} → {dispute.end_city}
                    </span>
                  </div>
                  <p className="font-semibold text-gray-800">{dispute.passenger_name}</p>
                  <p className="text-sm text-gray-500">{dispute.passenger_email}</p>
                </div>
                <div className="text-right text-sm text-gray-400">
                  <Clock size={14} className="inline mr-1" />
                  {dispute.created_at ? new Date(dispute.created_at).toLocaleDateString() : '-'}
                </div>
              </div>

              <div className="bg-gray-50 rounded-lg p-3 mb-3">
                <p className="text-sm font-semibold text-gray-600 mb-1">{t('disputes.disputeReason')}:</p>
                <p className="text-gray-800">{dispute.reason}</p>
              </div>

              {dispute.admin_notes && (
                <div className="bg-blue-50 rounded-lg p-3 mb-3">
                  <p className="text-sm font-semibold text-blue-600 mb-1">{t('disputes.adminNotes')}:</p>
                  <p className="text-gray-800">{dispute.admin_notes}</p>
                </div>
              )}

              {dispute.status === 'open' && (
                <div className="mt-4 pt-4 border-t">
                  <textarea
                    placeholder={t('disputes.adminNotes') + '...'}
                    value={resolving === dispute.id ? adminNotes : ''}
                    onChange={(e) => { setResolving(dispute.id); setAdminNotes(e.target.value); }}
                    onFocus={() => setResolving(dispute.id)}
                    className="w-full border border-gray-200 rounded-lg p-3 mb-3 text-sm focus:border-orange-400 outline-none"
                    rows="2"
                  />
                  <div className="flex gap-3">
                    <button
                      data-testid={`resolve-dispute-${dispute.id}`}
                      onClick={() => handleResolve(dispute.id, 'resolved')}
                      className="flex items-center gap-1.5 bg-green-600 text-white px-4 py-2 rounded-lg text-sm font-bold hover:bg-green-700 transition"
                    >
                      <Check size={16} /> {t('disputes.resolve')}
                    </button>
                    <button
                      data-testid={`reject-dispute-${dispute.id}`}
                      onClick={() => handleResolve(dispute.id, 'rejected')}
                      className="flex items-center gap-1.5 bg-red-600 text-white px-4 py-2 rounded-lg text-sm font-bold hover:bg-red-700 transition"
                    >
                      <X size={16} /> {t('disputes.reject')}
                    </button>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
